import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Alert,
  CircularProgress, Chip, Switch, FormControlLabel, Grid, Tooltip, Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Remove as RemoveIcon,
  ContentCopy,
  ElectricCar as EVIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth, vortxColors, useThemeMode } from '@vortx/shared-ui';

const API_BASE = process.env.REACT_APP_API_URL || '';

const CATEGORY_LABELS = {
  light_duty: 'Light-Duty',
  medium_duty: 'Medium-Duty',
  heavy_duty: 'Heavy-Duty',
  bus: 'Bus',
  van: 'Van',
};

const DEFAULT_CURVE = [
  { soc: 0, power_kw: 100 },
  { soc: 50, power_kw: 80 },
  { soc: 80, power_kw: 40 },
  { soc: 100, power_kw: 0 },
];

const EMPTY_VEHICLE = {
  id: '', name: '', category: 'light_duty',
  battery_kwh: '', efficiency_kwh_per_mile: '',
  max_ac_charge_kw: '', max_dc_charge_kw: '',
  charging_curve: null,
};

const EVVehicleDatabasePage = () => {
  const { currentUser } = useAuth();
  const { isRetro } = useThemeMode();

  // Data state
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Editor dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null); // null = creating new
  const [formData, setFormData] = useState({ ...EMPTY_VEHICLE });
  const [validationErrors, setValidationErrors] = useState([]);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // --- API helpers ---

  const authHeaders = useCallback(async () => {
    const token = await currentUser.getIdToken();
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [currentUser]);

  const fetchVehicles = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE}/api/flux_ev/v1/ev/vehicles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch vehicles: ${res.statusText}`);
      const data = await res.json();
      setVehicles(data.vehicles || []);
      setCategories(data.categories || Object.keys(CATEGORY_LABELS));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) fetchVehicles();
  }, [currentUser, fetchVehicles]);

  // Clear success message after 4 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // --- Filtering ---

  const filteredVehicles = useMemo(() => {
    let list = vehicles;
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      list = list.filter(v =>
        v.name?.toLowerCase().includes(lower) || v.id?.toLowerCase().includes(lower)
      );
    }
    if (filterCategory) {
      list = list.filter(v => v.category === filterCategory);
    }
    return list;
  }, [vehicles, searchTerm, filterCategory]);

  // --- Validation ---

  const validate = () => {
    const errs = [];
    if (!formData.id || !/^[a-z0-9_]+$/.test(formData.id)) {
      errs.push('ID is required and must contain only lowercase letters, numbers, and underscores.');
    }
    if (!formData.name?.trim()) {
      errs.push('Name is required.');
    }
    if (!formData.category) {
      errs.push('Category is required.');
    }
    const batt = parseFloat(formData.battery_kwh);
    if (!batt || batt <= 0) {
      errs.push('Battery (kWh) must be a positive number.');
    }
    const eff = parseFloat(formData.efficiency_kwh_per_mile);
    if (!eff || eff <= 0) {
      errs.push('Efficiency (kWh/mi) must be a positive number.');
    }
    const acKw = parseFloat(formData.max_ac_charge_kw);
    if (isNaN(acKw) || acKw < 0) {
      errs.push('Max AC charge (kW) must be a number >= 0.');
    }
    if (!formData.charging_curve) {
      const dcKw = parseFloat(formData.max_dc_charge_kw);
      if (!dcKw || dcKw <= 0) {
        errs.push('Max DC charge (kW) must be a positive number when no curve is defined.');
      }
    }
    if (formData.charging_curve) {
      if (formData.charging_curve.length < 2) {
        errs.push('Charging curve must have at least 2 breakpoints.');
      }
      for (let i = 0; i < formData.charging_curve.length; i++) {
        const pt = formData.charging_curve[i];
        if (pt.soc < 0 || pt.soc > 100) {
          errs.push(`Breakpoint ${i + 1}: SOC must be between 0 and 100.`);
        }
        if (pt.power_kw < 0) {
          errs.push(`Breakpoint ${i + 1}: Power must be >= 0.`);
        }
      }
    }
    return errs;
  };

  // --- Curve helpers ---

  const peakPowerFromCurve = (curve) => {
    if (!curve || curve.length === 0) return 0;
    return Math.max(...curve.map(p => p.power_kw));
  };

  const handleCurveToggle = (enabled) => {
    if (enabled) {
      const curve = [...DEFAULT_CURVE];
      const peak = peakPowerFromCurve(curve);
      setFormData(prev => ({
        ...prev,
        charging_curve: curve,
        max_dc_charge_kw: peak,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        charging_curve: null,
        max_dc_charge_kw: '',
      }));
    }
  };

  const handleCurvePointChange = (index, field, value) => {
    const numVal = value === '' ? '' : parseFloat(value);
    setFormData(prev => {
      const newCurve = prev.charging_curve.map((pt, i) =>
        i === index ? { ...pt, [field]: numVal } : pt
      );
      const peak = peakPowerFromCurve(newCurve);
      return {
        ...prev,
        charging_curve: newCurve,
        max_dc_charge_kw: peak,
      };
    });
  };

  const sortCurveBySOC = () => {
    setFormData(prev => {
      if (!prev.charging_curve) return prev;
      const sorted = [...prev.charging_curve].sort((a, b) => {
        const aVal = a.soc === '' ? -1 : a.soc;
        const bVal = b.soc === '' ? -1 : b.soc;
        return aVal - bVal;
      });
      return { ...prev, charging_curve: sorted };
    });
  };

  const addCurvePoint = () => {
    setFormData(prev => {
      const curve = prev.charging_curve || [];
      const lastSoc = curve.length > 0 ? Math.max(...curve.map(p => p.soc === '' ? 0 : p.soc)) : 0;
      const newSoc = Math.min(lastSoc + 10, 100);
      const newCurve = [...curve, { soc: newSoc, power_kw: 50 }];
      newCurve.sort((a, b) => {
        const aVal = a.soc === '' ? -1 : a.soc;
        const bVal = b.soc === '' ? -1 : b.soc;
        return aVal - bVal;
      });
      const peak = peakPowerFromCurve(newCurve);
      return {
        ...prev,
        charging_curve: newCurve,
        max_dc_charge_kw: peak,
      };
    });
  };

  const removeCurvePoint = (index) => {
    setFormData(prev => {
      const newCurve = prev.charging_curve.filter((_, i) => i !== index);
      const peak = peakPowerFromCurve(newCurve);
      return {
        ...prev,
        charging_curve: newCurve,
        max_dc_charge_kw: peak,
      };
    });
  };

  // --- Dialog handlers ---

  const openCreateDialog = () => {
    setEditingVehicle(null);
    setFormData({ ...EMPTY_VEHICLE });
    setValidationErrors([]);
    setDialogOpen(true);
  };

  const openEditDialog = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      id: vehicle.id || '',
      name: vehicle.name || '',
      category: vehicle.category || 'light_duty',
      battery_kwh: vehicle.battery_kwh ?? '',
      efficiency_kwh_per_mile: vehicle.efficiency_kwh_per_mile ?? '',
      max_ac_charge_kw: vehicle.max_ac_charge_kw ?? '',
      max_dc_charge_kw: vehicle.max_dc_charge_kw ?? '',
      charging_curve: vehicle.charging_curve ? vehicle.charging_curve.map(p => ({ ...p })) : null,
    });
    setValidationErrors([]);
    setDialogOpen(true);
  };

  const openDuplicateDialog = (vehicle) => {
    setEditingVehicle(null);
    setFormData({
      id: vehicle.id ? vehicle.id + '_copy' : '',
      name: vehicle.name ? vehicle.name + ' (Copy)' : '',
      category: vehicle.category || 'light_duty',
      battery_kwh: vehicle.battery_kwh ?? '',
      efficiency_kwh_per_mile: vehicle.efficiency_kwh_per_mile ?? '',
      max_ac_charge_kw: vehicle.max_ac_charge_kw ?? '',
      max_dc_charge_kw: vehicle.max_dc_charge_kw ?? '',
      charging_curve: vehicle.charging_curve ? vehicle.charging_curve.map(p => ({ ...p })) : null,
    });
    setValidationErrors([]);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingVehicle(null);
    setFormData({ ...EMPTY_VEHICLE });
    setValidationErrors([]);
  };

  const handleSave = async () => {
    const errs = validate();
    if (errs.length > 0) {
      setValidationErrors(errs);
      return;
    }
    setValidationErrors([]);
    setSaving(true);
    setError(null);

    try {
      const headers = await authHeaders();
      const payload = {
        ...formData,
        battery_kwh: parseFloat(formData.battery_kwh),
        efficiency_kwh_per_mile: parseFloat(formData.efficiency_kwh_per_mile),
        max_ac_charge_kw: parseFloat(formData.max_ac_charge_kw),
        max_dc_charge_kw: parseFloat(formData.max_dc_charge_kw),
        charging_curve: formData.charging_curve
          ? formData.charging_curve.map(p => ({
              soc: parseFloat(p.soc),
              power_kw: parseFloat(p.power_kw),
            }))
          : null,
      };

      let res;
      if (editingVehicle) {
        res = await fetch(`${API_BASE}/api/flux_ev/v1/ev/vehicles/${editingVehicle.id}`, {
          method: 'PUT', headers, body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API_BASE}/api/flux_ev/v1/ev/vehicles`, {
          method: 'POST', headers, body: JSON.stringify(payload),
        });
      }

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to save vehicle: ${res.statusText}`);
      }

      setSuccess(editingVehicle ? 'Vehicle updated successfully.' : 'Vehicle created successfully.');
      closeDialog();
      fetchVehicles();
    } catch (err) {
      setValidationErrors([err.message]);
    } finally {
      setSaving(false);
    }
  };

  // --- Delete handlers ---

  const openDeleteDialog = (vehicle) => {
    setVehicleToDelete(vehicle);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setVehicleToDelete(null);
  };

  const handleDelete = async () => {
    if (!vehicleToDelete) return;
    setDeleting(true);
    setError(null);

    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/api/flux_ev/v1/ev/vehicles/${vehicleToDelete.id}`, {
        method: 'DELETE', headers,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to delete vehicle: ${res.statusText}`);
      }
      setSuccess(`Deleted "${vehicleToDelete.name}".`);
      closeDeleteDialog();
      fetchVehicles();
    } catch (err) {
      setError(err.message);
      closeDeleteDialog();
    } finally {
      setDeleting(false);
    }
  };

  // --- Form field helpers ---

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // --- Render ---

  return (
    <AdminLayout title="EV Vehicle Database" requireSuperAdmin>
      <Box sx={{ mb: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        {/* Toolbar */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search vehicles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filterCategory}
              label="Category"
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <MenuItem value="">All Categories</MenuItem>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
            sx={{ bgcolor: vortxColors.gold, '&:hover': { bgcolor: alpha(vortxColors.gold, 0.85) } }}
          >
            Add Vehicle
          </Button>
        </Box>

        {/* Vehicle table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filteredVehicles.length === 0 ? (
          <Alert severity="info">
            {vehicles.length === 0
              ? 'No vehicles in the database yet. Click "Add Vehicle" to create one.'
              : 'No vehicles match the current filter.'}
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Battery (kWh)</TableCell>
                  <TableCell align="right">Efficiency (kWh/mi)</TableCell>
                  <TableCell align="right">Max DC (kW)</TableCell>
                  <TableCell align="center">Curve</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVehicles.map((vehicle) => (
                  <TableRow
                    key={vehicle.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openEditDialog(vehicle)}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {vehicle.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: vortxColors.stone, fontSize: '0.7rem' }}>
                        {vehicle.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={CATEGORY_LABELS[vehicle.category] || vehicle.category}
                        size="small"
                        sx={{
                          bgcolor: alpha(vortxColors.teal, 0.15),
                          color: vortxColors.teal,
                          border: `1px solid ${alpha(vortxColors.teal, 0.3)}`,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{vehicle.battery_kwh}</TableCell>
                    <TableCell align="right">{vehicle.efficiency_kwh_per_mile}</TableCell>
                    <TableCell align="right">{vehicle.max_dc_charge_kw}</TableCell>
                    <TableCell align="center">
                      {vehicle.charging_curve ? (
                        <CheckIcon sx={{ color: vortxColors.gold, fontSize: 18 }} />
                      ) : (
                        <Typography variant="body2" sx={{ color: vortxColors.stone }}>--</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(vehicle)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton size="small" onClick={() => openDuplicateDialog(vehicle)}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => openDeleteDialog(vehicle)} sx={{ color: '#e74c3c' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Vehicle Editor Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EVIcon />
          {editingVehicle ? `Edit: ${editingVehicle.name}` : 'Add New Vehicle'}
        </DialogTitle>
        <DialogContent dividers>
          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationErrors.map((err, i) => (
                <div key={i}>{err}</div>
              ))}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Row 1: ID and Name */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="ID"
                fullWidth
                size="small"
                value={formData.id}
                onChange={(e) => handleFieldChange('id', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                disabled={!!editingVehicle}
                helperText="Lowercase, numbers, underscores only"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Name"
                fullWidth
                size="small"
                value={formData.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleFieldChange('category', e.target.value)}
                >
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Row 2: Numeric specs */}
            <Grid item xs={6} sm={3}>
              <TextField
                label="Battery (kWh)"
                fullWidth
                size="small"
                type="number"
                value={formData.battery_kwh}
                onChange={(e) => handleFieldChange('battery_kwh', e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Efficiency (kWh/mi)"
                fullWidth
                size="small"
                type="number"
                inputProps={{ step: 0.01 }}
                value={formData.efficiency_kwh_per_mile}
                onChange={(e) => handleFieldChange('efficiency_kwh_per_mile', e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Max AC (kW)"
                fullWidth
                size="small"
                type="number"
                value={formData.max_ac_charge_kw}
                onChange={(e) => handleFieldChange('max_ac_charge_kw', e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Max DC (kW)"
                fullWidth
                size="small"
                type="number"
                value={formData.max_dc_charge_kw}
                disabled={!!formData.charging_curve}
                helperText={formData.charging_curve ? 'Auto-set from curve peak' : ''}
                onChange={(e) => handleFieldChange('max_dc_charge_kw', e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Charging Curve Section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                DC Charging Curve
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.charging_curve}
                    onChange={(e) => handleCurveToggle(e.target.checked)}
                    color="primary"
                  />
                }
                label={formData.charging_curve ? 'Curve enabled' : 'No curve (flat power)'}
              />
            </Box>

            {formData.charging_curve && (
              <Grid container spacing={2}>
                {/* Breakpoint Table */}
                <Grid item xs={12} md={5}>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '40%', whiteSpace: 'nowrap', fontSize: '0.8rem', py: 1, px: 1.5 }}>SOC (%)</TableCell>
                          <TableCell sx={{ width: '40%', whiteSpace: 'nowrap', fontSize: '0.8rem', py: 1, px: 1.5 }}>Power (kW)</TableCell>
                          <TableCell sx={{ width: '20%', py: 1, px: 0.5 }} align="center"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.charging_curve.map((pt, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ py: 0.5, px: 1 }}>
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{ min: 0, max: 100, step: 1 }}
                                value={pt.soc}
                                onChange={(e) => handleCurvePointChange(idx, 'soc', e.target.value)}
                                onBlur={sortCurveBySOC}
                                sx={{ '& .MuiInputBase-input': { py: 0.5, px: 1, fontSize: '0.85rem' } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5, px: 1 }}>
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{ min: 0, step: 1 }}
                                value={pt.power_kw}
                                onChange={(e) => handleCurvePointChange(idx, 'power_kw', e.target.value)}
                                sx={{ '& .MuiInputBase-input': { py: 0.5, px: 1, fontSize: '0.85rem' } }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => removeCurvePoint(idx)}
                                disabled={formData.charging_curve.length <= 2}
                                sx={{ color: '#e74c3c' }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addCurvePoint}
                    sx={{ mt: 1 }}
                  >
                    Add Breakpoint
                  </Button>
                </Grid>

                {/* Curve Chart */}
                <Grid item xs={12} md={7}>
                  <Box sx={{ height: 300, border: '1px solid', borderColor: alpha(vortxColors.teal, 0.3), borderRadius: 1, p: 1 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={
                          [...(formData.charging_curve || [])]
                            .filter(p => p.soc !== '' && p.power_kw !== '')
                            .sort((a, b) => a.soc - b.soc)
                        }
                        margin={{ top: 10, right: 20, bottom: 20, left: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(vortxColors.teal, 0.2)} />
                        <XAxis
                          dataKey="soc"
                          type="number"
                          domain={[0, 100]}
                          label={{ value: 'SOC (%)', position: 'insideBottom', offset: -10, style: { fill: vortxColors.stone, fontSize: 12 } }}
                          tick={{ fill: vortxColors.stone, fontSize: 11 }}
                        />
                        <YAxis
                          label={{ value: 'Power (kW)', angle: -90, position: 'insideLeft', offset: 5, style: { fill: vortxColors.stone, fontSize: 12 } }}
                          tick={{ fill: vortxColors.stone, fontSize: 11 }}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: isRetro ? '#1a1a1a' : '#fff',
                            border: `1px solid ${vortxColors.teal}`,
                            borderRadius: 4,
                            color: '#c8c8c8',
                            fontSize: 12,
                          }}
                          formatter={(value) => [`${value} kW`, 'Power']}
                          labelFormatter={(val) => `SOC: ${val}%`}
                        />
                        <ReferenceLine
                          y={parseFloat(formData.max_dc_charge_kw) || 0}
                          stroke={vortxColors.gold}
                          strokeDasharray="5 5"
                          label={{
                            value: `Max: ${formData.max_dc_charge_kw} kW`,
                            position: 'right',
                            style: { fill: vortxColors.gold, fontSize: 11 },
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="power_kw"
                          stroke={vortxColors.teal}
                          strokeWidth={2}
                          dot={{ fill: vortxColors.teal, r: 4 }}
                          activeDot={{ r: 6, fill: vortxColors.gold }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: vortxColors.gold, '&:hover': { bgcolor: alpha(vortxColors.gold, 0.85) } }}
          >
            {saving ? <CircularProgress size={20} /> : (editingVehicle ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Vehicle</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{vehicleToDelete?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default EVVehicleDatabasePage;
